import aiLogger from './ai-logger';

/**
 * Modal de perguntas interativas para o fluxo de DOCUMENTAÇÃO.
 *
 * Diferente do AskQuestionsModal (chat), este é desenhado para funcionar
 * DENTRO do popup #ai-review-modal, com:
 * - Overlay fixed (dimmer fullscreen sobre o popup)
 * - Sem interferência em sidebars do chat
 * - CSS isolado (id: doc-ask-questions-styles)
 * - Mesma API backend: POST /api/ai/ask-questions/respond
 *
 * Fluxo:
 * 1. Backend emite [TOOL_PROGRESS]{type:'ask_questions_pending', ...}
 * 2. ai-actions.ts recebe e chama this.docAskModal.showQuestions(payload)
 * 3. Usuário responde → POST /api/ai/ask-questions/respond
 * 4. Backend AskQuestionsService detecta resposta no Redis e continua
 */

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface QuestionOption {
    label: string;
    value: string;
    recommended?: boolean;
    allowCustom?: boolean;
}

interface Question {
    id: string;
    question: string;
    options?: QuestionOption[];
    allowCustom?: boolean;
    customPlaceholder?: string;
}

interface QuestionPayload {
    requestId: string;
    questions: Question[];
    context?: string;
}

interface Answer {
    questionId: string;
    answer: string;
    isCustom: boolean;
}

// ─── Modal ───────────────────────────────────────────────────────────────────

export class DocAskQuestionsModal {
    private overlay!: HTMLDivElement;
    private modal!: HTMLDivElement;
    private contextEl!: HTMLDivElement;
    private bodyEl!: HTMLDivElement;
    private confirmBtn!: HTMLButtonElement;

    private currentPayload: QuestionPayload | null = null;
    private answers: Map<string, string> = new Map();
    private customInputs: Map<string, string> = new Map();
    private isVisible: boolean = false;

    constructor() {
        this.createModal();
        this.injectStyles();
        this.preventClosing();
    }

    // ─── DOM Creation ────────────────────────────────────────────────────

    private createModal(): void {
        this.overlay = document.createElement('div');
        this.overlay.className = 'doc-ask-overlay hidden';

        this.modal = document.createElement('div');
        this.modal.className = 'doc-ask-modal';

        const header = document.createElement('div');
        header.className = 'doc-ask-header';
        header.innerHTML = '&#x1F914; Athena precisa de informações';

        this.contextEl = document.createElement('div');
        this.contextEl.className = 'doc-ask-context hidden';

        this.bodyEl = document.createElement('div');
        this.bodyEl.className = 'doc-ask-body';

        const footer = document.createElement('div');
        footer.className = 'doc-ask-footer';

        this.confirmBtn = document.createElement('button');
        this.confirmBtn.className = 'doc-ask-confirm-btn';
        this.confirmBtn.textContent = 'Confirmar Respostas';
        this.confirmBtn.disabled = true;
        this.confirmBtn.addEventListener('click', () => this.confirmAnswers());

        footer.appendChild(this.confirmBtn);
        this.modal.appendChild(header);
        this.modal.appendChild(this.contextEl);
        this.modal.appendChild(this.bodyEl);
        this.modal.appendChild(footer);
        this.overlay.appendChild(this.modal);

        // Overlay vai no body (position: fixed, z-index alto)
        document.body.appendChild(this.overlay);
    }

    // ─── Public API ──────────────────────────────────────────────────────

    /**
     * Exibe o modal com as perguntas recebidas do backend.
     */
    public showQuestions(payload: QuestionPayload): void {
        this.currentPayload = payload;
        this.answers.clear();
        this.customInputs.clear();

        if (payload.context) {
            this.contextEl.textContent = `ℹ️  ${payload.context}`;
            this.contextEl.classList.remove('hidden');
        } else {
            this.contextEl.classList.add('hidden');
        }

        this.renderQuestions(payload.questions);
        this.overlay.classList.remove('hidden');
        this.isVisible = true;
    }

    /**
     * Fecha o modal (chamado externamente no timeout ou cancelamento).
     */
    public dismiss(): void {
        this.overlay.classList.add('hidden');
        this.isVisible = false;
        this.currentPayload = null;
        this.answers.clear();
        this.customInputs.clear();
        this.confirmBtn.textContent = 'Confirmar Respostas';
        this.confirmBtn.disabled = true;
    }

    /**
     * Remove o overlay do DOM completamente (cleanup).
     */
    public destroy(): void {
        this.dismiss();
        this.overlay.remove();
    }

    // ─── Rendering ───────────────────────────────────────────────────────

    private renderQuestions(questions: Question[]): void {
        this.bodyEl.innerHTML = '';
        questions.forEach((question, index) => {
            const block = this.createQuestionBlock(question, index + 1, questions.length);
            this.bodyEl.appendChild(block);
        });
    }

    private createQuestionBlock(question: Question, index: number, total: number): HTMLElement {
        const block = document.createElement('div');
        block.className = 'doc-ask-question-block';

        const number = document.createElement('div');
        number.className = 'doc-ask-question-number';
        number.textContent = `Pergunta ${index} de ${total}`;
        block.appendChild(number);

        const text = document.createElement('div');
        text.className = 'doc-ask-question-text';
        text.textContent = question.question;
        block.appendChild(text);

        // Opções predefinidas
        if (question.options && question.options.length > 0) {
            const optionsContainer = document.createElement('div');
            optionsContainer.className = 'doc-ask-options';
            optionsContainer.dataset.questionId = question.id;

            question.options.forEach(option => {
                const btn = this.createOptionButton(question.id, option);
                optionsContainer.appendChild(btn);
            });

            block.appendChild(optionsContainer);

            // Input inline para opções com allowCustom
            const hasCustomOption = question.options.some(o => o.allowCustom);
            if (hasCustomOption) {
                block.appendChild(this.createInlineCustomInput(question));
            }
        }

        // Campo de texto livre (allowCustom sem options)
        if (question.allowCustom) {
            block.appendChild(this.createFreeTextInput(question));
        }

        return block;
    }

    private createOptionButton(questionId: string, option: QuestionOption): HTMLElement {
        const btn = document.createElement('button');
        btn.className = 'doc-ask-option';
        btn.dataset.value = option.value;
        if (option.recommended) btn.classList.add('recommended');
        if (option.allowCustom) btn.dataset.allowCustom = 'true';

        const radio = document.createElement('span');
        radio.className = 'doc-ask-radio';
        radio.textContent = '○';

        const label = document.createElement('span');
        label.className = 'doc-ask-option-label';
        label.textContent = option.label;

        btn.appendChild(radio);
        btn.appendChild(label);

        if (option.recommended) {
            const badge = document.createElement('span');
            badge.className = 'doc-ask-badge';
            badge.textContent = 'Recomendado';
            btn.appendChild(badge);
        }

        btn.addEventListener('click', () => this.selectOption(questionId, option.value, btn));
        return btn;
    }

    private createInlineCustomInput(question: Question): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'doc-ask-inline-custom hidden';
        wrapper.dataset.questionId = question.id;

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 150;
        input.placeholder = question.customPlaceholder || 'Especifique...';
        input.className = 'doc-ask-inline-input';

        input.addEventListener('input', () => {
            const val = input.value.trim();
            if (val) {
                this.answers.set(question.id, val);
            } else {
                this.answers.delete(question.id);
            }
            this.checkAllAnswered();
        });

        wrapper.appendChild(input);
        return wrapper;
    }

    private createFreeTextInput(question: Question): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'doc-ask-free-text';

        const inputRow = document.createElement('div');
        inputRow.className = 'doc-ask-input-row';
        inputRow.dataset.questionId = question.id;

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 150;
        input.placeholder = question.customPlaceholder || 'Outra resposta...';
        input.dataset.questionId = question.id;

        const submitBtn = document.createElement('button');
        submitBtn.className = 'doc-ask-submit-btn';
        submitBtn.textContent = '✔';
        submitBtn.disabled = true;

        // Botão-opção (modo exibição após confirmar)
        const optionBtn = document.createElement('button');
        optionBtn.className = 'doc-ask-option doc-ask-custom-option hidden';
        optionBtn.dataset.questionId = question.id;

        const radio = document.createElement('span');
        radio.className = 'doc-ask-radio';
        radio.textContent = '○';

        const optionLabel = document.createElement('span');
        optionLabel.className = 'doc-ask-option-label';

        const editIcon = document.createElement('span');
        editIcon.className = 'doc-ask-edit-icon';
        editIcon.title = 'Editar';
        editIcon.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.5 7.5-.707-.707 7-7 .707.707zM1 13.5V15h1.5l4.407-4.407-1.5-1.5z"/></svg>';

        optionBtn.appendChild(radio);
        optionBtn.appendChild(optionLabel);
        optionBtn.appendChild(editIcon);

        // Input event: atualiza resposta
        input.addEventListener('input', () => {
            const val = input.value.trim();
            submitBtn.disabled = !val;
            if (val) {
                this.customInputs.set(question.id, val);
            } else {
                this.customInputs.delete(question.id);
            }
            // Sem opções predefinidas: atualiza diretamente
            if (!question.options || question.options.length === 0) {
                if (val) {
                    this.answers.set(question.id, val);
                } else {
                    this.answers.delete(question.id);
                }
                this.checkAllAnswered();
            }
        });

        // Confirmar texto customizado
        const confirmCustom = () => {
            const value = input.value.trim();
            if (!value) return;
            this.customInputs.set(question.id, value);
            optionBtn.dataset.value = value;
            optionLabel.textContent = value;
            inputRow.classList.add('hidden');
            optionBtn.classList.remove('hidden');
            this.selectOption(question.id, value, optionBtn);
        };

        input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') confirmCustom();
        });
        submitBtn.addEventListener('click', confirmCustom);

        // Clique no botão-opção: seleciona
        optionBtn.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('.doc-ask-edit-icon')) return;
            const value = this.customInputs.get(question.id) || '';
            if (value) this.selectOption(question.id, value, optionBtn);
        });

        // Clique no ícone editar: volta ao input
        editIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            if (optionBtn.classList.contains('selected')) {
                optionBtn.classList.remove('selected');
                this.answers.delete(question.id);
                this.checkAllAnswered();
            }
            optionBtn.classList.add('hidden');
            inputRow.classList.remove('hidden');
            input.value = this.customInputs.get(question.id) || '';
            submitBtn.disabled = !input.value.trim();
            input.focus();
        });

        inputRow.appendChild(input);
        inputRow.appendChild(submitBtn);
        wrapper.appendChild(inputRow);
        wrapper.appendChild(optionBtn);
        return wrapper;
    }

    // ─── Selection Logic ─────────────────────────────────────────────────

    private selectOption(questionId: string, value: string, btn: HTMLElement): void {
        this.deselectOptions(questionId);

        btn.classList.add('selected');
        const radio = btn.querySelector('.doc-ask-radio');
        if (radio) radio.textContent = '●';

        // Se opção tem allowCustom, mostrar input inline
        const inlineWrapper = this.bodyEl.querySelector<HTMLElement>(`.doc-ask-inline-custom[data-question-id="${questionId}"]`);
        if (inlineWrapper) {
            if (btn.dataset.allowCustom === 'true') {
                inlineWrapper.classList.remove('hidden');
                const inlineInput = inlineWrapper.querySelector<HTMLInputElement>('input');
                if (inlineInput) {
                    inlineInput.focus();
                    const existing = inlineInput.value.trim();
                    if (existing) {
                        this.answers.set(questionId, existing);
                    } else {
                        this.answers.delete(questionId);
                    }
                    this.checkAllAnswered();
                    return;
                }
            } else {
                inlineWrapper.classList.add('hidden');
            }
        }

        // Limpa input customizado se selecionou opção normal
        const inputRow = this.bodyEl.querySelector<HTMLElement>(`.doc-ask-input-row[data-question-id="${questionId}"]`);
        if (inputRow && !inputRow.classList.contains('hidden')) {
            const input = inputRow.querySelector('input') as HTMLInputElement;
            if (input) {
                input.value = '';
                input.style.borderColor = '';
            }
            this.customInputs.delete(questionId);
            const sub = inputRow.querySelector('.doc-ask-submit-btn') as HTMLButtonElement;
            if (sub) sub.disabled = true;
        }

        this.answers.set(questionId, value);
        this.checkAllAnswered();
    }

    private deselectOptions(questionId: string): void {
        const container = this.bodyEl.querySelector<HTMLElement>(`.doc-ask-options[data-question-id="${questionId}"]`);
        if (container) {
            container.querySelectorAll<HTMLElement>('.doc-ask-option').forEach(btn => {
                btn.classList.remove('selected');
                const radio = btn.querySelector('.doc-ask-radio');
                if (radio) radio.textContent = '○';
            });
        }
        const customBtn = this.bodyEl.querySelector<HTMLElement>(`.doc-ask-custom-option[data-question-id="${questionId}"]`);
        if (customBtn) customBtn.classList.remove('selected');

        const inlineWrapper = this.bodyEl.querySelector<HTMLElement>(`.doc-ask-inline-custom[data-question-id="${questionId}"]`);
        if (inlineWrapper) inlineWrapper.classList.add('hidden');
    }

    private checkAllAnswered(): void {
        if (!this.currentPayload) return;
        const allAnswered = this.currentPayload.questions.every(q => this.answers.has(q.id));
        this.confirmBtn.disabled = !allAnswered;
    }

    // ─── Submit ──────────────────────────────────────────────────────────

    private async confirmAnswers(): Promise<void> {
        if (!this.currentPayload) return;
        this.checkAllAnswered();
        if (this.confirmBtn.disabled) return;

        const answersArray: Answer[] = Array.from(this.answers.entries()).map(([questionId, answer]) => ({
            questionId,
            answer,
            isCustom: this.customInputs.has(questionId),
        }));

        try {
            this.confirmBtn.disabled = true;
            this.confirmBtn.textContent = 'Enviando...';

            const response = await window.$http.post('/api/ai/ask-questions/respond', {
                request_id: this.currentPayload.requestId,
                answers: answersArray,
            });

            const responseData = response.data as { success: boolean; message?: string };

            if (responseData.success) {
                this.dismiss();
            } else {
                throw new Error(responseData.message || 'Erro ao enviar respostas');
            }
        } catch (error) {
            aiLogger.error('[DocAskQuestionsModal] Erro ao enviar respostas:', error);
            if (window.$events) {
                window.$events.error('Erro ao enviar respostas. Tente novamente.');
            }
            this.confirmBtn.disabled = false;
            this.confirmBtn.textContent = 'Confirmar Respostas';
        }
    }

    // ─── Event Prevention ────────────────────────────────────────────────

    private preventClosing(): void {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true); // capture phase para interceptar antes do popup

        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                e.preventDefault();
                e.stopPropagation();
            }
        });
    }

    // ─── Styles ──────────────────────────────────────────────────────────

    private injectStyles(): void {
        if (document.getElementById('doc-ask-questions-styles')) return;
        const style = document.createElement('style');
        style.id = 'doc-ask-questions-styles';
        style.textContent = `
            .doc-ask-overlay {
                position: fixed;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 999999;
                background: var(--chat-modal-backdrop, rgba(0, 0, 0, 0.45));
                backdrop-filter: blur(3px);
                -webkit-backdrop-filter: blur(3px);
            }

            .doc-ask-overlay.hidden {
                display: none !important;
            }

            .doc-ask-modal {
                background: var(--chat-modal-bg, var(--chat-container-bg, rgba(255, 255, 255, 0.92)));
                backdrop-filter: blur(30px);
                -webkit-backdrop-filter: blur(30px);
                border: 1px solid var(--chat-modal-border, rgba(255, 255, 255, 0.3));
                box-shadow: 0 12px 48px rgba(0, 0, 0, 0.25);
                border-radius: 22px;
                max-width: 460px;
                width: calc(100% - 48px);
                max-height: 80vh;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                color: var(--chat-modal-text, var(--chat-text-color, inherit));
                animation: docAskFadeIn 0.2s ease;
            }

            @keyframes docAskFadeIn {
                from { opacity: 0; transform: scale(0.96) translateY(8px); }
                to   { opacity: 1; transform: scale(1) translateY(0); }
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-modal {
                    background: var(--chat-modal-bg, var(--chat-container-bg, rgba(30, 30, 30, 0.95)));
                    box-shadow: 0 8px 36px rgba(0, 0, 0, 0.5);
                    border-color: var(--chat-modal-border, rgba(255, 255, 255, 0.08));
                }
            }

            .doc-ask-header {
                padding: 18px 24px 16px;
                background: var(--chat-modal-header-bg, rgba(0, 0, 0, 0.06));
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border-bottom: 1px solid rgba(0, 0, 0, 0.07);
                font-size: 15px;
                font-weight: 700;
                letter-spacing: -0.2px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-header {
                    background: var(--chat-modal-header-bg, rgba(255, 255, 255, 0.05));
                    border-bottom-color: rgba(255, 255, 255, 0.06);
                }
            }

            .doc-ask-context {
                padding: 12px 24px;
                background: rgba(0, 0, 0, 0.03);
                border-bottom: 1px solid rgba(0, 0, 0, 0.07);
                color: #555;
                font-size: 13px;
                line-height: 1.5;
            }

            .doc-ask-context.hidden { display: none !important; }

            @media (prefers-color-scheme: dark) {
                .doc-ask-context {
                    background: rgba(255,255,255,0.03);
                    border-bottom-color: rgba(255,255,255,0.07);
                    color: #aaa;
                }
            }

            .doc-ask-body {
                padding: 20px;
                overflow-y: auto;
                overflow-x: hidden;
                flex: 1;
            }

            .doc-ask-body * {
                box-sizing: border-box;
            }

            .doc-ask-footer {
                padding: 16px 24px 20px;
                border-top: 1px solid rgba(0, 0, 0, 0.07);
                display: flex;
                justify-content: center;
                background: rgba(0, 0, 0, 0.02);
                border-radius: 0 0 22px 22px;
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-footer {
                    border-top-color: rgba(255,255,255,0.07);
                    background: rgba(255,255,255,0.02);
                }
            }

            .doc-ask-confirm-btn {
                padding: 12px 36px;
                background: var(--chat-btn-color, var(--color-primary));
                color: #fff;
                border: none;
                border-radius: 12px;
                font-size: 15px;
                font-weight: 600;
                cursor: pointer;
                transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
            }

            .doc-ask-confirm-btn:disabled {
                opacity: 0.4;
                cursor: not-allowed;
            }

            .doc-ask-confirm-btn:not(:disabled):hover {
                opacity: 0.88;
                transform: translateY(-1px);
                box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22) !important;
            }

            /* ── Question blocks ── */
            .doc-ask-question-block { margin-bottom: 28px; }

            .doc-ask-question-number {
                font-size: 11px;
                font-weight: 700;
                color: var(--color-primary);
                text-transform: uppercase;
                letter-spacing: 0.8px;
                margin-bottom: 6px;
                opacity: 0.8;
            }

            .doc-ask-question-text {
                font-size: 15px;
                font-weight: 600;
                color: #1a1a1a;
                margin-bottom: 14px;
                line-height: 1.4;
                word-wrap: break-word;
                overflow-wrap: break-word;
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-question-text { color: #eee; }
            }

            /* ── Options ── */
            .doc-ask-options {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .doc-ask-option {
                width: 100%;
                padding: 12px 16px;
                background: var(--chat-card-bg, rgba(255, 255, 255, 0.6));
                border: 1.5px solid rgba(0, 0, 0, 0.1);
                border-radius: 10px;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 10px;
                font-size: 14px;
                color: var(--chat-text-color, #333);
                font-weight: 500;
                transition: all 0.15s ease;
                text-align: left;
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-option {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06));
                    border-color: rgba(255,255,255,0.1);
                    color: var(--chat-text-color, #ddd);
                }
            }

            .doc-ask-option:hover:not(.selected) {
                border-color: var(--color-primary);
                background: rgba(var(--color-primary-rgb, 0,0,0), 0.05);
                transform: translateX(3px);
            }

            .doc-ask-option.selected {
                border-color: var(--color-primary);
                background: rgba(var(--color-primary-rgb, 0,0,0), 0.08);
                color: var(--color-primary);
                font-weight: 600;
            }

            .doc-ask-option.recommended {
                border-color: var(--color-positive, #0f7d15);
            }

            .doc-ask-option.recommended.selected {
                border-color: var(--color-positive, #0f7d15);
                background: rgba(15, 125, 21, 0.08);
                color: var(--color-positive, #0f7d15);
            }

            .doc-ask-radio {
                font-size: 16px;
                flex-shrink: 0;
            }

            .doc-ask-option-label { flex: 1; }

            .doc-ask-badge {
                padding: 3px 8px;
                background: var(--color-positive, #0f7d15);
                color: white;
                border-radius: 8px;
                font-size: 10px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.4px;
                flex-shrink: 0;
            }

            /* ── Inline custom input (for options with allowCustom) ── */
            .doc-ask-inline-custom {
                margin-top: 8px;
                animation: docAskSlideIn 0.18s ease;
            }

            .doc-ask-inline-custom.hidden { display: none; }

            @keyframes docAskSlideIn {
                from { opacity: 0; transform: translateY(-4px); }
                to   { opacity: 1; transform: translateY(0); }
            }

            .doc-ask-inline-input {
                width: 100%;
                padding: 11px 14px;
                border: 1.5px solid var(--color-primary);
                border-radius: 10px;
                font-size: 14px;
                background: var(--chat-card-bg, rgba(255,255,255,0.6));
                color: var(--chat-text-color, #333);
                outline: none;
                box-sizing: border-box;
                box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb, 0,0,200), 0.08);
            }

            .doc-ask-inline-input:focus {
                box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb, 0,0,200), 0.18);
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-inline-input {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06));
                    color: var(--chat-text-color, #ddd);
                }
            }

            /* ── Free text input ── */
            .doc-ask-free-text {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-top: 8px;
            }

            .doc-ask-input-row {
                display: flex;
                gap: 8px;
            }

            .doc-ask-input-row.hidden { display: none; }

            .doc-ask-input-row input {
                flex: 1;
                min-width: 0;
                padding: 11px 14px;
                border: 1.5px solid rgba(0, 0, 0, 0.1);
                border-radius: 10px;
                font-size: 14px;
                background: var(--chat-card-bg, rgba(255,255,255,0.6));
                color: var(--chat-text-color, #333);
                transition: all 0.15s;
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-input-row input {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06));
                    border-color: rgba(255,255,255,0.1);
                    color: var(--chat-text-color, #ddd);
                }
            }

            .doc-ask-input-row input:focus {
                outline: none;
                border-color: var(--color-primary);
            }

            .doc-ask-submit-btn {
                padding: 0 14px;
                background: var(--chat-btn-color, var(--color-primary));
                color: white;
                border: none;
                border-radius: 10px;
                cursor: pointer;
                font-size: 16px;
                min-width: 44px;
                transition: opacity 0.15s;
            }

            .doc-ask-submit-btn:disabled {
                opacity: 0.35;
                cursor: not-allowed;
            }

            /* ── Custom option (after confirm) ── */
            .doc-ask-custom-option {
                border-color: var(--color-primary);
                border-style: dashed;
            }

            .doc-ask-custom-option .doc-ask-radio {
                font-size: 14px;
            }

            .doc-ask-custom-option.hidden { display: none; }

            .doc-ask-edit-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                border-radius: 5px;
                background: rgba(0, 0, 0, 0.07);
                flex-shrink: 0;
                transition: background 0.15s;
                cursor: pointer;
                margin-left: auto;
            }

            .doc-ask-edit-icon:hover {
                background: rgba(0, 0, 0, 0.14);
            }

            @media (prefers-color-scheme: dark) {
                .doc-ask-edit-icon { background: rgba(255, 255, 255, 0.10); }
                .doc-ask-edit-icon:hover { background: rgba(255, 255, 255, 0.18); }
            }
        `;
        document.head.appendChild(style);
    }
}
